import { apolloClient, apolloClientSSR } from 'graphql/apollo-client';
import { BRAND, CATEGORIES } from 'graphql/queries';
import revalidateServerSideCache from 'utils/revalidateServerSideCache';
import useScrollRestoration from 'hooks/UI/useScrollRestoration';
import usePagination from 'hooks/products/usePagination';
import useBrandCatalogue from 'hooks/brands/useBrandCatalogue';
import useBrandImages from 'hooks/brands/useBrandImages';
import { Box, textAlign } from '@xstyled/styled-components';
import SEO from '@/SEO';
import BrandBanner from '@/Banner/BrandBanner';
import ProductCard from '@/UI/ProductCard';
import Layout from '@/UI/Layout';
import { Container } from '@/UI/Layout/Container';
import { FlexBox } from '@/UI/Primitives/FlexBox';
import Pagination from '@/Catalogue/Pagination';
import SkeletonProductCard from '@/UI/Skeletons/SkeletonProductCard';
import MobileSkeletonProductCard from '@/UI/Skeletons/MobileSkeletonProductCard';
import MobileProductCard from '@/UI/ProductCard/MobileProductCard';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

export default function Brand({ brand, categories, locale }) {
  // All hooks must be called before any early returns
  const { products, loading, pages, count } = useBrandCatalogue();
  const { bannerLink } = useBrandImages(brand?.description);
  useScrollRestoration({ data: products });
  usePagination();

  // Safety check for brand AFTER hooks
  if (!brand) {
    return (
      <Layout categories={categories}>
        <SEO
          seo={{
            title: 'Brand not found - Baltbaby',
            description: 'The requested brand was not found.'
          }}
        />
        <Container>
          <Box p="2rem" textAlign="center">
            <h1>Brand not found</h1>
            <p>The requested brand could not be found.</p>
          </Box>
        </Container>
      </Layout>
    );
  }

  let et;
  let lt;
  let en;
  let desc = brand.short_description;

  // Parse multilingual description safely
  if (brand && brand.short_description) {
    if (desc.includes('[lt]')) {
      const splitLT = desc.split('[lt]');
      lt = splitLT[1];
      et = splitLT[0];
      if (lt && lt.includes('[en]')) {
        const splitLT2 = lt.split('[en]');
        lt = splitLT2[0];
        en = splitLT2[1];
      }
    }
    if (!desc.includes('[lt]') && desc.includes('[en]')) {
      const splitLT3 = desc.split('[en]');
      en = splitLT3[1];
      et = splitLT3[0];
    }
    
    // Set description based on locale
    if (locale) {
      switch (locale) {
        case 'lt':
          desc = lt || et || brand.short_description;
          break;
        case 'en':
          desc = en || et || brand.short_description;
          break;
        case 'et':
        default:
          desc = et || brand.short_description;
          break;
      }
    } else {
      desc = et || brand.short_description;
    }
  }

  // Fallback if no description is set
  if (!desc) {
    desc = brand.short_description || '';
  }

  const skeletons = [...Array(24)];

  return (
    <Layout categories={categories}>
      <SEO
        seo={{
          title: `${brand?.value || 'Brand'} - Baltbaby`,
          ...(desc ? { description: desc } : null)
        }}
      />

      {/* Banner */}
      <BrandBanner brand={brand} description={desc} image={bannerLink} />
      <div id="product_container"></div>
      
      {/* Mobile */}
      <Container display={{ _: 'block', desktop: 'none' }} mb={72}>
        <Box
          display={{ _: 'grid', desktop: 'none' }}
          gridTemplateColumns={{ _: '1fr', lg_tablet: '1fr 1fr' }}
          justifyItems={{ sm_tablet: 'center' }}
          columnGap="0.75rem"
          rowGap="0.1rem"
          p={{ _: '0 1rem', desktop: 0 }}
        >
          {loading ? (
            <>
              {skeletons.map((_, i) => (
                <MobileSkeletonProductCard key={i} maxW={450} minH={150} />
              ))}
            </>
          ) : (
            <>
              {products?.map(product => {
                if (product !== null && product !== undefined) {
                  return (
                    <MobileProductCard
                      key={product?.sku || Math.random()}
                      product={product}
                      maxW={450}
                      minH={150}
                    />
                  );
                }
                return null;
              })}
            </>
          )}
        </Box>
        {pages > 0 && count > 24 && products && products?.length !== count && (
          <FlexBox align="center" justify="center">
            <Pagination pages={pages} />
          </FlexBox>
        )}
      </Container>

      {/* Desktop */}
      <Container display={{ _: 'none', desktop: 'block' }} mb={72}>
        <Box
          id="brand_products_desktop"
          display={{ _: 'none', desktop: 'grid' }}
          gridTemplateColumns={{
            desktop: 'repeat(3, 33.33%)',
            lg_desktop: 'repeat(4, 25%)'
          }}
          columnGap="1rem"
          rowGap="2rem"
          p="1.5rem 1rem 0 0"
        >
          {loading ? (
            <>
              {skeletons.map((_, i) => (
                <SkeletonProductCard key={i} />
              ))}
            </>
          ) : (
            <>
              {products?.map((product, i) => {
                if (product !== null && product !== undefined) {
                  return (
                    <ProductCard
                      key={product?.sku || i}
                      product={product}
                      imageSize={{ width: 1000, height: 1040 }}
                      w={300}
                      border="none"
                    />
                  );
                }
                return null;
              })}
            </>
          )}
        </Box>
        {pages > 0 && count > 24 && products && products?.length !== count && (
          <FlexBox align="center" justify="center">
            <Pagination pages={pages} />
          </FlexBox>
        )}
      </Container>
    </Layout>
  );
}

export async function getServerSideProps(context) {
  try {
    revalidateServerSideCache(context.res);
    
    const query = (query, variables) => {
      return apolloClientSSR(context.locale).query({
        query,
        variables,
        context: {
          fetchOptions: { method: 'GET' }
        }
      });
    };

    const categoriesPromise = query(CATEGORIES, { ids: '2' });
    const brandPromise = apolloClient.query({
      query: BRAND,
      variables: { urlKey: context.query?.brand },
      context: { fetchOptions: { method: 'GET' } }
    });

    const [brandData, categories] = await Promise.all([
      brandPromise,
      categoriesPromise
    ]);

    // Extract brand data safely
    const brand = brandData?.data?.mpbrand?.items?.[0] || null;
    
    // Extract categories safely
    const categoriesData = categories?.data?.categoryList?.[0]?.children || [];

    return {
      props: {
        brand,
        categories: categoriesData,
        locale: context.locale || 'et',
        ...(await serverSideTranslations(context.locale || 'et'))
      }
    };
  } catch (error) {
    console.error('getServerSideProps error:', error);
    
    // Return safe fallback props
    return {
      props: {
        brand: null,
        categories: [],
        locale: context.locale || 'et',
        ...(await serverSideTranslations(context.locale || 'et'))
      }
    };
  }
}